import React from 'react';
import { injectIntl } from '../reactIntl';

/**
 * Higher-Order Component that injects translated bio into component props
 * Useful for class components that cannot use hooks
 * 
 * @param {React.Component} Component - The component to wrap
 * @returns {React.Component} Wrapped component with translatedBio prop
 * 
 * @example
 * class MyComponent extends React.Component {
 *   render() {
 *     const { translatedBio } = this.props;
 *     return <div>{translatedBio}</div>;
 *   }
 * }
 * export default withTranslatedBio(MyComponent);
 */
export const withTranslatedBio = (Component) => {
  const WithTranslatedBio = (props) => {
    const { user, intl } = props;
    const currentLocale = intl?.locale || 'sv';

    let translatedBio = '';
    if (user?.attributes?.profile) {
      const profile = user.attributes.profile;
      const originalBio = profile.bio || '';

      // Source language (Swedish) - return original
      if (currentLocale === 'sv') {
        translatedBio = originalBio;
      } else {
        // Try to get translated bio from protectedData
        const bioTranslations = profile.protectedData?.bioTranslations || {};
        translatedBio = bioTranslations[currentLocale] || originalBio;
      }
    }

    return <Component {...props} translatedBio={translatedBio} />;
  };

  WithTranslatedBio.displayName = `WithTranslatedBio(${Component.displayName || Component.name || 'Component'})`;

  return injectIntl(WithTranslatedBio);
};

/**
 * Get translated bio directly (utility function)
 * 
 * @param {Object} user - The user object
 * @param {string} locale - The target locale
 * @returns {string} Translated bio or original
 */
export const getTranslatedBio = (user, locale = 'sv') => {
  if (!user?.attributes?.profile) {
    return '';
  }

  const profile = user.attributes.profile;
  const originalBio = profile.bio || '';

  if (locale === 'sv') {
    return originalBio;
  }

  const bioTranslations = profile.protectedData?.bioTranslations || {};
  return bioTranslations[locale] || originalBio;
};
